/**
 * MnbPlayer - Main player component
 * Orchestrates video playback with HLS.js for Smart TVs
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { usePlayer, usePlayerState } from '../core/PlayerContext';
import { HLSProvider } from '../providers/HLSProvider';
import { VideoProvider } from '../providers/VideoProvider';

/**
 * Main player component
 */
function MnbPlayer(props) {
    var {
        source,
        poster,
        subtitle,
        behavior,
        autoPlay = true,
        children,
        onReady,
        onPlay,
        onPause,
        onTimeUpdate,
        onEnded,
        onError,
        className = '',
    } = props;

    var player = usePlayer();
    var videoRef = useRef(null);
    var hlsProviderRef = useRef(null);
    var videoProviderRef = useRef(null);
    var isInitializedRef = useRef(false);
    var previousSourceRef = useRef('');
    var subtitleRef = useRef(subtitle); // Track subtitle without triggering effect

    // Subscribe to state for callbacks
    var isReady = usePlayerState('isReady');
    var playing = usePlayerState('playing');
    var paused = usePlayerState('paused');
    var currentTime = usePlayerState('currentTime');
    var ended = usePlayerState('ended');
    var error = usePlayerState('error');

    // Trigger callbacks on state changes
    useEffect(function () {
        if (isReady && onReady) onReady();
    }, [isReady, onReady]);

    useEffect(function () {
        if (playing && onPlay) onPlay();
    }, [playing, onPlay]);

    useEffect(function () {
        if (paused && !ended && onPause) onPause();
    }, [paused, ended, onPause]);

    useEffect(function () {
        if (onTimeUpdate) onTimeUpdate({ currentTime: currentTime });
    }, [currentTime, onTimeUpdate]);

    useEffect(function () {
        if (ended && onEnded) onEnded();
    }, [ended, onEnded]);

    useEffect(function () {
        if (error && onError) onError(error);
    }, [error, onError]);

    // Apply saved subtitle styles on mount
    useEffect(function () {
        try {
            var savedFontSize = localStorage.getItem('mnb-subtitle-fontSize');
            var savedTextColor = localStorage.getItem('mnb-subtitle-textColor');
            var savedBgColor = localStorage.getItem('mnb-subtitle-bgColor');

            var fontSize = savedFontSize ? Number(savedFontSize) : 1;
            var textColor = savedTextColor || '#ffffff';
            var bgColor = savedBgColor || 'rgba(0, 0, 0, 0.5)';
            // Base font size ~40px for TV readability
            var fontSizePx = Math.round(40 * fontSize);

            // Create or update the style element
            var styleId = 'mnb-cue-style';
            var style = document.getElementById(styleId);
            if (!style) {
                style = document.createElement('style');
                style.id = styleId;
                document.head.appendChild(style);
            }

            style.textContent =
                'video::cue {' +
                'color: ' + textColor + ' !important;' +
                'background-color: ' + bgColor + ' !important;' +
                'font-size: ' + fontSizePx + 'px !important;' +
                'font-family: \"AnjomanMax\", sans-serif !important;' +
                '}' +
                // WebKit-specific selectors for older browsers
                'video::-webkit-media-text-track-display {' +
                'display: inline-block !important;' +
                'background-color: transparent !important;' +
                'font-size: ' + fontSizePx + 'px !important;' +
                'text-align: center !important;' +
                '}' +
                'video::-webkit-media-text-track-container {' +
                'font-size: ' + fontSizePx + 'px !important;' +
                'bottom: 10vh !important;' +
                'top: auto !important;' +
                'text-align: center !important;' +
                '}';

            console.log('[MnbPlayer] Applied saved subtitle styles - fontSize:', fontSizePx, 'color:', textColor, 'bg:', bgColor);
        } catch (e) {
            // Ignore localStorage errors
            console.log('[MnbPlayer] Could not load saved subtitle styles:', e);
        }
    }, []);

    // Initialize providers
    var initializePlayer = useCallback(function (videoEl, sourceUrl, sourceType, subtitles) {
        console.log('[MnbPlayer] Initializing with source:', sourceUrl, 'type:', sourceType);

        // Create VideoProvider
        var videoProvider = new VideoProvider(videoEl, player.state);
        videoProvider.setup();
        videoProviderRef.current = videoProvider;

        // Store video ref in context
        player.videoRef.current = videoEl;

        // Restore persisted mute setting
        try {
            var savedMuted = localStorage.getItem('mnb-player-muted');
            if (savedMuted !== null) {
                videoEl.muted = savedMuted === '1';
                player.state.set('muted', savedMuted === '1');
                console.log('[MnbPlayer] Restored mute setting:', savedMuted === '1');
            }
        } catch (e) { }

        // Check source type
        var isHLS = sourceType && (
            sourceType.indexOf('mpegurl') !== -1 ||
            sourceType.indexOf('mpegURL') !== -1
        ) || (sourceUrl && sourceUrl.indexOf('.m3u8') !== -1);

        if (isHLS && HLSProvider.isSupported()) {
            console.log('[MnbPlayer] Using HLS.js provider');

            // Create HLS provider
            var hlsProvider = new HLSProvider(videoEl, player.state);
            hlsProvider.setup();
            hlsProvider.loadSource(sourceUrl);
            hlsProviderRef.current = hlsProvider;

            // Store HLS ref in context
            player.hlsRef.current = hlsProvider.getInstance();

            // Restore persisted quality after manifest is parsed
            try {
                var savedQuality = localStorage.getItem('mnb-player-quality');
                if (savedQuality !== null) {
                    var Hls = window.Hls;
                    if (Hls) {
                        var hlsInstance = hlsProvider.getInstance();
                        var onManifest = function () {
                            var qualityIndex = parseInt(savedQuality, 10);
                            // Validate that the saved quality index exists in the manifest
                            if (qualityIndex >= -1 && qualityIndex < hlsInstance.levels.length) {
                                hlsInstance.currentLevel = qualityIndex;
                                player.state.set('currentQuality', qualityIndex);
                                console.log('[MnbPlayer] Restored quality setting:', qualityIndex);
                            }
                            hlsInstance.off(Hls.Events.MANIFEST_PARSED, onManifest);
                        };
                        hlsInstance.on(Hls.Events.MANIFEST_PARSED, onManifest);
                    }
                }
            } catch (e) { }

        } else if (isHLS && HLSProvider.isNativeHLSSupported()) {
            console.log('[MnbPlayer] Using native HLS');
            videoEl.src = sourceUrl;

        } else {
            console.log('[MnbPlayer] Using direct source (MP4)');
            videoProvider.setSource(sourceUrl, sourceType || 'video/mp4');
        }

        // Autoplay
        if (autoPlay) {
            setTimeout(function () {
                videoProvider.play().catch(function (err) {
                    console.warn('[MnbPlayer] Autoplay failed:', err.message);
                });
            }, 100);
        }

        // Add external subtitle tracks if provided
        if (subtitles && subtitles.length > 0) {
            console.log('[MnbPlayer] Adding external subtitles:', subtitles.length);
            
            var defaultSubLang = behavior && behavior.defaultSubLang;
            var processedSubtitles = subtitles.map(function(item, index) {
                var srcLang = item.srcLang || item.srclang || item.lang;
                var label = item.label;
                var lang = item.lang;
                
                var isDefaultSub = defaultSubLang && (srcLang === defaultSubLang || label === defaultSubLang || lang === defaultSubLang);
                var isDefaultSubByFallback = defaultSubLang && subtitles.filter(function(x) {
                    var xLang = x.srcLang || x.srclang || x.lang;
                    return xLang === defaultSubLang || x.label === defaultSubLang || x.lang === defaultSubLang;
                }).length < 1 && index === 0;

                return Object.assign({}, item, {
                    default: isDefaultSub || isDefaultSubByFallback || false
                });
            });

            videoProvider.addTextTracks(processedSubtitles);
        }

        isInitializedRef.current = true;
    }, [player, autoPlay]);

    // Handle source changes
    useEffect(function () {
        var videoEl = videoRef.current;
        if (!videoEl) return;

        // Parse source
        var sourceUrl = '';
        var sourceType = '';

        if (Array.isArray(source)) {
            var srcObj = source[0];
            if (srcObj) {
                sourceUrl = srcObj.src || srcObj;
                sourceType = srcObj.type || '';
            }
        } else if (typeof source === 'object' && source && source.src) {
            sourceUrl = source.src;
            sourceType = source.type || '';
        } else if (typeof source === 'string') {
            sourceUrl = source;
        }

        if (!sourceUrl) {
            console.warn('[MnbPlayer] No valid source');
            return;
        }

        // Skip if same source
        if (sourceUrl === previousSourceRef.current) {
            return;
        }
        previousSourceRef.current = sourceUrl;

        // Cleanup previous
        if (hlsProviderRef.current) {
            hlsProviderRef.current.destroy();
            hlsProviderRef.current = null;
        }
        if (videoProviderRef.current) {
            videoProviderRef.current.destroy();
            videoProviderRef.current = null;
        }

        // Reset state
        player.state.reset();

        // Initialize with subtitle from ref (not prop to avoid re-runs)
        initializePlayer(videoEl, sourceUrl, sourceType, subtitleRef.current);

    }, [source, player, initializePlayer]); // subtitle removed - uses ref instead

    // Cleanup on unmount
    useEffect(function () {
        return function () {
            console.log('[MnbPlayer] Unmounting, cleaning up...');
            previousSourceRef.current = '';

            if (hlsProviderRef.current) {
                hlsProviderRef.current.destroy();
                hlsProviderRef.current = null;
            }
            if (videoProviderRef.current) {
                videoProviderRef.current.destroy();
                videoProviderRef.current = null;
            }
        };
    }, []);

    return (
        React.createElement('div', {
            id: 'mnb-player-container',
            className: 'mnb-player-container ' + className,
            style: {
                position: 'relative',
                width: '100%',
                height: '100%',
                backgroundColor: '#000',
                overflow: 'hidden',
            },
        },
            React.createElement('video', {
                ref: videoRef,
                className: 'mnb-player-video',
                poster: poster,
                playsInline: true,
                preload: 'metadata',
                // Required for loading external VTT subtitles from CDN
                crossOrigin: 'anonymous',
                style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                },
            }),
            children
        )
    );
}

export { MnbPlayer };
